import React from "react";
import { Icon } from "../core/Icon.jsx";
import { Select } from "../forms/Select.jsx";

/* Pagination — token-driven, keyboard-operable page control.
   page is 1-based. Renders prev/next + a windowed set of page buttons with
   aria-current on the active page. Optional rows-per-page Select when
   pageSizeOptions is supplied. Replaces the legacy MUI Pagination /
   TablePagination triplet (REG-378 part 3, FR-017). */

function pageWindow(page, pageCount, siblingCount) {
  const pages = [];
  const start = Math.max(1, page - siblingCount);
  const end = Math.min(pageCount, page + siblingCount);
  if (start > 1) pages.push(1);
  if (start > 2) pages.push("…");
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < pageCount - 1) pages.push("…");
  if (end < pageCount) pages.push(pageCount);
  return pages;
}

const btnBase = {
  minWidth: 32,
  height: 32,
  padding: "0 8px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  background: "var(--surface-card)",
  color: "var(--text-body)",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: 13,
  cursor: "pointer",
};

export function Pagination({
  page = 1,
  pageCount = 1,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  totalCount,
  siblingCount = 1,
  style,
  ...rest
}) {
  const hasPageSizeSelector = Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0;
  const showNav = pageCount > 1;

  const entrySummary =
    totalCount != null && pageSize != null
      ? totalCount === 0
        ? "0 entries"
        : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount} entries`
      : null;

  if (!showNav && !hasPageSizeSelector && !entrySummary) return null;

  const go = (p) => {
    if (p >= 1 && p <= pageCount && p !== page && onPageChange) onPageChange(p);
  };

  const handlePageSizeChange = (value) => {
    const next = Number(value);
    if (Number.isNaN(next) || next === pageSize) return;
    if (onPageSizeChange) onPageSizeChange(next);
    if (page !== 1 && onPageChange) onPageChange(1);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--inline-gap)",
        flexWrap: "wrap",
        width: "100%",
        paddingTop: "var(--space-sm)",
        ...style,
      }}
      {...rest}
    >
      {hasPageSizeSelector ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: 13,
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            Rows per page
          </span>
          <Select
            aria-label="Rows per page"
            size="sm"
            value={pageSize != null ? String(pageSize) : undefined}
            options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
            onChange={handlePageSizeChange}
            style={{ minWidth: 72, width: 72 }}
          />
          {entrySummary ? (
            <span
              style={{
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: 12,
                color: "var(--text-muted)",
                whiteSpace: "nowrap",
              }}
            >
              {entrySummary}
            </span>
          ) : null}
        </div>
      ) : entrySummary ? (
        <span
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: 12,
            color: "var(--text-muted)",
            whiteSpace: "nowrap",
          }}
        >
          {entrySummary}
        </span>
      ) : null}
      {showNav ? (
        <nav
          aria-label="Pagination"
          style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
        >
          <button
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => go(page - 1)}
            style={{ ...btnBase, opacity: page <= 1 ? 0.5 : 1, cursor: page <= 1 ? "default" : "pointer" }}
          >
            <Icon name="chevronLeft" size={16} />
          </button>
          {pageWindow(page, pageCount, siblingCount).map((p, i) =>
            p === "…" ? (
              // eslint-disable-next-line react/no-array-index-key
              <span key={`gap-${i}`} style={{ color: "var(--text-muted)", padding: "0 4px" }}>
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                aria-label={`Page ${p}`}
                aria-current={p === page ? "page" : undefined}
                onClick={() => go(p)}
                style={{
                  ...btnBase,
                  borderColor: p === page ? "var(--green-success)" : "var(--border)",
                  color: p === page ? "var(--text-strong)" : "var(--text-body)",
                  fontWeight: p === page ? 700 : 500,
                }}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= pageCount}
            onClick={() => go(page + 1)}
            style={{ ...btnBase, opacity: page >= pageCount ? 0.5 : 1, cursor: page >= pageCount ? "default" : "pointer" }}
          >
            <Icon name="chevronRight" size={16} />
          </button>
        </nav>
      ) : null}
    </div>
  );
}

export default Pagination;
